import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, Menu, X, ShoppingCart, User, Globe, Camera, Package, Shield, TrendingUp, Headphones } from 'lucide-react';
import { Star, Video, Tag, ArrowRight, Heart, Check, Building, ExternalLink, MessageCircle } from 'lucide-react';
import FirebaseProductService from '../../../services/product.service';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

const LandingPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('top-ranking');
  const [favorites, setFavorites] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const frequentSearches = [
    'iphones 15 pro max',
    'mobile phones',
    'watch',
    'laptop',
    'headphones'
  ];

  const valueProps = [
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Millions of business offerings',
      description: 'Explore products and suppliers for your business from millions of offerings worldwide.'
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Assured quality and transactions',
      description: 'Ensure product quality from verified suppliers, with your orders protected from payment to delivery.'
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: 'One-stop trading solution',
      description: 'Order seamlessly from product/supplier search to order management, payment, and fulfillment.'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Tailored trading experience',
      description: 'Get curated benefits, such as exclusive discounts, enhanced protection, and extra support, to help grow your business every step of the way.'
    }
  ];

  const categories = [
    { 
      name: 'Machinery',
      image: 'https://images.pexels.com/photos/4225118/pexels-photo-4225118.jpeg?auto=compress&cs=tinysrgb&dpr=1&w=500',
      emoji: '🏭'
    },
    { 
      name: 'Apparel',
      image: 'https://www.cheersagar.com/images/blog/16436498188567.jpg',
      emoji: '👕'
    },
    { 
      name: 'Consumer Electronics',
      image: 'https://sp-ao.shortpixel.ai/client/to_auto,q_glossy,ret_img,w_800,h_586/https://ikonic.com/wp-content/uploads/2023/10/industries-consumer-electronics.jpeg',
      emoji: '📱'
    },
    { 
      name: 'Home & Garden',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQ8yRudto_KRSwx6yKNmaa-GAS9oI8eN9VhcQ&s',
      emoji: '🏠'
    },
    { 
      name: 'Sports & Entertainment',
      image: 'https://cdn.prod.website-files.com/600faaecb03ce57f6fa98050/6037d22eb60b9e2aedbdbc97_DDM-Mar-20-SEM%20Career%20Preview.jpg',
      emoji: '⚽'
    },
    { 
      name: 'Vehicles & Accessories',
      image: 'https://autobacsindia.com/wp-content/uploads/2025/01/car_accessories.png',
      emoji: '🚗'
    },
    { 
      name: 'Toys & Hobbies',
      image: 'https://dam.mediacorp.sg/image/upload/s---8jdIi_i--/f_auto,q_auto/c_fill,g_auto,h_622,w_830/v1/tdy-migration/feng3817-01.jpg?itok=731l_Bb7',
      emoji: '👶'
    },
    { 
      name: 'Beauty & Personal Care',
      image: 'https://5.imimg.com/data5/SELLER/Default/2022/8/XN/NF/SB/157564005/beauty-personal-care.jpg',
      emoji: '💄'
    },
    { 
      name: 'Tools & Hardware',
      image: 'https://img.freepik.com/free-photo/top-view-mechanical-tools-arrangement_23-2149552411.jpg?semt=ais_hybrid&w=740&q=80',
      emoji: '🔧'
    },
    { 
      name: 'Lights & Lighting',
      image: 'https://imgstaticcontent.lbb.in/lbbnew/wp-content/uploads/sites/2/2016/08/Feature.jpg',
      emoji: '💡'
    },
    { 
      name: 'Packaging & Printing',
      image: 'https://whynotmedia.in/wp-content/uploads/2020/05/printing-page-main-Banner-image-copy.jpg',
      emoji: '📦'
    },
    { 
      name: 'Gifts & Crafts',
      image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQWIMh7fw77kPUC7zK0kBRp9Y_NUbSINBt4tA&s',
      emoji: '🎨'
    }
  ];

  const tabs = [
    { id: 'top-ranking', label: 'Top ranking', icon: TrendingUp },
    { id: 'new-arrivals', label: 'New arrivals', icon: Tag },
    { id: 'video-show', label: 'Video show', icon: Video },
    { id: 'top-deals', label: 'Top deals', icon: Star }
  ];

  // Fetch approved products from Firebase
  useEffect(() => {
    const fetchApprovedProducts = async () => {
      try {
        setLoading(true);
        const result = await FirebaseProductService.getAllProducts({ limitCount: 20 });
        
        // Filter only approved products
        const approvedProducts = result.products.filter(product => 
          product.status === 'APPROVED' || product.status === 'ACTIVE'
        );
        
        // Transform the data to match the UI structure - updated for ProductsPage card design
        const transformedProducts = approvedProducts.map((product) => {
          // Get the main image URL
          let imageUrl = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop';
          if (product.images && product.images.length > 0) {
            const mainImage = product.images.find(img => img.isMain) || product.images[0];
            imageUrl = mainImage.data || mainImage.url || imageUrl;
          }
          
          // Generate badge based on product properties
          const badges = ['Top Seller', 'New', 'Hot Deal', 'Trending', 'Top Rated', 'Best Price', 'Popular', 'New Arrival', 'Featured', 'Hot'];
          const badge = badges[Math.floor(Math.random() * badges.length)];
          
          return {
            id: product.id,
            image: imageUrl,
            title: product.name || 'Untitled Product',
            price: product.price || Math.floor(Math.random() * 500000) + 100000,
            rating: product.rating || (Math.random() * 0.5 + 4.5).toFixed(1),
            reviews: product.reviews || Math.floor(Math.random() * 4000) + 500,
            badge: badge,
            verified: product.sellerVerified || true,
            sellerName: product.sellerName || 'Verified Supplier',
            description: product.description || 'Premium quality product with competitive pricing',
            category: product.category || 'General',
            specifications: product.specifications || [],
            images: product.images || [],
            sellerId: product.sellerId,
            sellerEmail: product.sellerEmail,
            moq: product.moq || Math.floor(Math.random() * 100) + 10,
            unit: product.unit || 'PCS',
            businessType: product.businessType || 'Manufacturer',
            city: product.city || 'Not Specified',
            stock: product.stock || Math.floor(Math.random() * 1000),
            status: 'APPROVED'
          };
        });
        
        setProducts(transformedProducts);
        setFilteredProducts(transformedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        const defaultProducts = getDefaultProducts();
        setProducts(defaultProducts);
        setFilteredProducts(defaultProducts);
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedProducts();
  }, []);

  // Handle search
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredProducts(products);
      setIsSearching(false);
    } else {
      const filtered = products.filter(product =>
        product.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredProducts(filtered);
      setIsSearching(true);
    }
  }, [searchQuery, products]);

  // Default products in case Firebase fails
  const getDefaultProducts = () => {
    return [
      {
        id: 1,
        image: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop',
        title: 'Sarbati White Sella Rice',
        price: 500000,
        rating: 4.8,
        reviews: 2283,
        badge: 'Top Seller',
        verified: true,
        sellerName: 'Premium Agro Foods',
        description: 'Premium quality basmati rice',
        category: 'Food Products',
        specifications: ['Premium Quality', 'Export Grade', 'ISO Certified'],
        images: [{ data: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop' }],
        moq: 47,
        unit: 'PCS',
        businessType: 'Manufacturer',
        city: 'Delhi',
        stock: 1000,
        status: 'APPROVED'
      },
      {
        id: 2,
        image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400&h=400&fit=crop',
        title: 'Honda City Car',
        price: 850000,
        rating: 4.5,
        reviews: 864,
        badge: 'New',
        verified: true,
        sellerName: 'Auto Motors India',
        description: 'Latest model sedan car',
        category: 'Automotive',
        specifications: ['2024 Model', 'Automatic Transmission', 'Diesel Engine'],
        images: [{ data: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=400&h=400&fit=crop' }],
        moq: 82,
        unit: 'PCS',
        businessType: 'Distributor',
        city: 'Mumbai',
        stock: 50,
        status: 'APPROVED'
      },
      {
        id: 3,
        image: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=400&h=400&fit=crop',
        title: 'Tata Steel Rods',
        price: 65000,
        rating: 4.7,
        reviews: 3406,
        badge: 'Hot Deal',
        verified: true,
        sellerName: 'Steel Manufacturing Co.',
        description: 'High tensile strength steel rods',
        category: 'Construction',
        specifications: ['Grade 60', '12mm Diameter', 'ISI Certified'],
        images: [{ data: 'https://images.unsplash.com/photo-1491553895911-0055eca6402d?w=400&h=400&fit=crop' }],
        moq: 23,
        unit: 'PCS',
        businessType: 'Manufacturer',
        city: 'Kolkata',
        stock: 5000,
        status: 'APPROVED'
      },
      {
        id: 4,
        image: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=400&h=400&fit=crop',
        title: 'Amul Dairy Products',
        price: 350000,
        rating: 4.9,
        reviews: 1567,
        badge: 'Trending',
        verified: true,
        sellerName: 'Amul Dairy',
        description: 'Fresh dairy products collection',
        category: 'Dairy',
        specifications: ['Pasteurized', 'ISO 22000', 'FSSAI Approved'],
        images: [{ data: 'https://images.unsplash.com/photo-1560343090-f0409e92791a?w=400&h=400&fit=crop' }],
        moq: 50,
        unit: 'PCS',
        businessType: 'Manufacturer',
        city: 'Ahmedabad',
        stock: 2000,
        status: 'APPROVED'
      },
      {
        id: 5,
        image: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop',
        title: 'Bajaj Electricals',
        price: 180000,
        rating: 4.6,
        reviews: 2134,
        badge: 'Top Rated',
        verified: true,
        sellerName: 'Bajaj Electricals Ltd',
        description: 'Energy efficient electrical appliances',
        category: 'Electronics',
        specifications: ['Energy Star Rated', '3 Year Warranty', 'BIS Certified'],
        images: [{ data: 'https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=400&h=400&fit=crop' }],
        moq: 35,
        unit: 'PCS',
        businessType: 'Manufacturer',
        city: 'Pune',
        stock: 800,
        status: 'APPROVED'
      }
    ];
  };

  const toggleFavorite = (id) => {
    setFavorites(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'Top Seller': 'bg-orange-50 text-orange-600 border-orange-200',
      'New': 'bg-blue-50 text-blue-600 border-blue-200',
      'Hot Deal': 'bg-red-50 text-red-600 border-red-200',
      'Trending': 'bg-purple-50 text-purple-600 border-purple-200',
      'Top Rated': 'bg-green-50 text-green-600 border-green-200',
      'Best Price': 'bg-yellow-50 text-yellow-700 border-yellow-200',
      'Popular': 'bg-pink-50 text-pink-600 border-pink-200',
      'New Arrival': 'bg-cyan-50 text-cyan-600 border-cyan-200',
      'Featured': 'bg-indigo-50 text-indigo-600 border-indigo-200',
      'Hot': 'bg-rose-50 text-rose-600 border-rose-200'
    };
    return colors[badge] || 'bg-gray-50 text-gray-600 border-gray-200';
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearching(false);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      setIsSearching(true);
    }
  };

  // Format INR currency
  const formatINR = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Handle contact supplier - redirect to login if not authenticated
  const handleContactSupplier = (product) => {
    if (!user) {
      navigate('/login', { state: { from: '/products', message: 'Please login to contact suppliers' } });
      return;
    }
    
    // Navigate to product details page or start chat
    navigate(`/buyer/products/${product.id}`, { state: { product } });
  };

  // Handle view details - redirect to product page
  const handleViewDetails = (product) => {
    navigate(`/buyer/products/${product.id}`, { state: { product } });
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: "url('https://images.wallpapersden.com/image/download/container-ship-in-san-pedro-california_bWVmbWWUmZqaraWkpJRoamVlrWZua20.jpg')"
          }}
        />
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          {/* Video/Learn Link */}
          <div className="mb-6">
            <button className="inline-flex items-center space-x-2 text-sm hover:text-orange-500 transition bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <div className="w-6 h-6 bg-white rounded-full flex items-center justify-center">
                <div className="w-0 h-0 border-l-4 border-l-black border-y-2 border-y-transparent ml-0.5"></div>
              </div>
              <span>Learn about CrossdealGlobal.com</span>
            </button>
          </div>

          {/* Main Heading */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-8 max-w-4xl leading-tight">
           India's Leading B2B Platform for Industrial Supplies & Manufacturing
          </h1>

          {/* Search Bar */}
          <div className="w-full max-w-4xl mx-auto relative">
            <div className="bg-white rounded-full shadow-2xl flex items-center p-1 md:p-2 w-full">
              <div className="flex items-center flex-1 px-4">
                <Search className="w-5 h-5 text-gray-400 mr-3" />
                <input
                  type="text"
                  placeholder="Search for products, suppliers, categories..."
                  className="flex-1 py-3 text-gray-900 outline-none border-none focus:outline-none focus:ring-0 focus:border-none text-sm md:text-base w-0 min-w-0"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                />
                {searchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                )}
              </div>
              <button className="p-2 md:p-3 hover:bg-gray-100 rounded-full flex-shrink-0">
                <Camera className="w-5 h-5 text-gray-600" />
              </button>
              <div className="h-6 w-px bg-gray-300 mx-1 md:mx-2"></div>
              <button 
                onClick={handleSearchSubmit}
                className="bg-orange-500 hover:bg-orange-600 text-white px-3 md:px-6 py-2 md:py-3 rounded-full font-semibold flex items-center space-x-1 md:space-x-2 flex-shrink-0"
              >
                <Search className="w-4 h-4 md:w-5 md:h-5" />
                <span className="text-sm md:text-base">Search</span>
              </button>
            </div>

            {/* Frequently Searched */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-xs md:text-sm whitespace-nowrap">Frequently searched:</span>
              <div className="flex flex-wrap gap-2">
                {frequentSearches.map((term, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setSearchQuery(term);
                      handleSearchSubmit();
                    }}
                    className="px-3 py-1.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-xs md:text-sm transition whitespace-nowrap"
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Results Section - Shows ONLY when searching */}
      {isSearching && (
        <section id="products-section" className="py-8 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            {/* Search results header */}
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Search Results for "{searchQuery}"
                  </h2>
                  <p className="text-gray-600 mt-1">
                    {filteredProducts.length} products found
                  </p>
                </div>
                <button
                  onClick={handleClearSearch}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                >
                  <X size={16} />
                  Clear search
                </button>
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                {[...Array(10)].map((_, index) => (
                  <div key={index} className="bg-white rounded-xl overflow-hidden border border-gray-200 animate-pulse">
                    <div className="aspect-square bg-gray-200"></div>
                    <div className="p-3 space-y-2">
                      <div className="h-4 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredProducts.length > 0 ? (
              <>
                {/* Product Grid - Using ProductsPage card design */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {filteredProducts.slice(0, 10).map((product) => (
                    <div
                      key={product.id}
                      className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                    >
                      {/* Product Image */}
                      <div className="relative h-40 sm:h-48 overflow-hidden bg-gray-100">
                        {product.image ? (
                          <img
                            src={product.image}
                            alt={product.title}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => {
                              e.target.src = `https://via.placeholder.com/300x200?text=${encodeURIComponent(product.title.substring(0, 20))}`;
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-200">
                            <Package className="h-12 w-12 text-gray-400" />
                          </div>
                        )}
                        
                        {/* Badge */}
                        <div className="absolute top-2 left-2">
                          <span className={`text-[10px] font-semibold px-2 py-1 rounded border ${getBadgeColor(product.badge)}`}>
                            {product.badge}
                          </span>
                        </div>

                        {/* Favorite Button */}
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFavorite(product.id);
                          }}
                          className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                        >
                          <Heart className="w-4 h-4 text-gray-600" />
                        </button>

                        {/* Verified Badge */}
                        {product.verified && (
                          <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1 text-[10px] font-medium">
                            <Shield size={10} />
                            Verified
                          </div>
                        )}
                      </div>

                      {/* Product Info - Updated to match ProductsPage */}
                      <div className="p-3 sm:p-4">
                        {/* Price and Rating */}
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <div className="text-lg font-bold text-orange-600">
                              {formatINR(product.price)}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              FOB Price / Piece
                            </div>
                          </div>
                          <div className="flex items-center bg-orange-50 px-2 py-0.5 rounded">
                            <Star className="w-3 h-3 text-orange-400 fill-current" />
                            <span className="text-xs font-semibold text-orange-700 ml-1">
                              {product.rating}
                            </span>
                          </div>
                        </div>

                        {/* Product Name */}
                        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 h-10">
                          {product.title}
                        </h3>

                        {/* Description Preview */}
                        {product.description && (
                          <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                            {product.description.substring(0, 60)}...
                          </p>
                        )}

                        {/* MOQ and Stock */}
                        <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                          <span>
                            <span className="font-medium">MOQ:</span> {product.moq} {product.unit}
                          </span>
                          {product.stock > 0 ? (
                            <span className="text-green-600 font-medium">In Stock</span>
                          ) : (
                            <span className="text-red-600 font-medium">Out of Stock</span>
                          )}
                        </div>

                        {/* Seller Info */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center">
                            <Building className="w-3 h-3 text-gray-400 mr-1.5" />
                            <span className="text-xs text-gray-600 truncate max-w-[100px]">
                              {product.sellerName}
                            </span>
                          </div>
                          {product.verified && (
                            <div className="flex items-center text-green-600 text-xs">
                              <Check className="w-3 h-3 mr-1" />
                              Verified
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="mt-4 space-y-2">
                          {/* <button
                            onClick={() => handleViewDetails(product)}
                            className="w-full py-2 text-sm font-medium text-orange-600 border border-orange-300 rounded hover:bg-orange-50 transition-colors flex items-center justify-center"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            View Details
                          </button> */}

                          <button
                            onClick={() => handleContactSupplier(product)}
                            className="w-full py-2.5 text-sm font-medium bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors flex items-center justify-center"
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            Contact Supplier
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* View More Button for Search Results */}
                {filteredProducts.length > 10 && (
                  <div className="text-center mt-10">
                    <button className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-orange-500 text-orange-600 font-semibold rounded-lg hover:bg-orange-500 hover:text-white transition-all group">
                      View all {filteredProducts.length} search results
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">No products found for "{searchQuery}"</div>
                <button 
                  onClick={handleClearSearch}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-all"
                >
                  View all products
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Regular Landing Page Content - Shows when NOT searching */}
      {!isSearching && (
        <>
          {/* Value Propositions - Dark Section */}
          <section className="bg-gradient-to-br from-orange-600 to-orange-900 text-white py-12">
            <div className="max-w-7xl mx-auto px-4">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {valueProps.map((prop, index) => (
                  <div key={index} className="space-y-3">
                    <div className="text-amber-300">
                      {prop.icon}
                    </div>
                    <h3 className="font-semibold text-lg">{prop.title}</h3>
                    <p className="text-sm text-gray-300 leading-relaxed">{prop.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Explore Offerings Section */}
          <section className="py-16 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-12">
                <div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2">
                    Explore millions of offerings
                  </h2>
                  <p className="text-lg md:text-xl text-gray-600">
                    tailored to your business needs
                  </p>
                </div>
                <div className="mt-6 lg:mt-0 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-orange-500">200M+</div>
                    <div className="text-gray-600">products</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-orange-500">200K+</div>
                    <div className="text-gray-600">suppliers</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-orange-500">5,900</div>
                    <div className="text-gray-600 text-sm">product categories</div>
                  </div>
                  <div>
                    <div className="text-2xl md:text-3xl font-bold text-orange-500">200+</div>
                    <div className="text-gray-600 text-sm">countries and regions</div>
                  </div>
                </div>
              </div>

              {/* Category Icons */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {categories.map((cat, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-4 md:p-6 text-center hover:shadow-lg transition cursor-pointer group"
                  >
                    <div className="w-18 h-18 md:w-18 md:h-18 mx-auto mb-2 md:mb-3 group-hover:scale-110 transition border-radius-2xl">
                      <img 
                        src={cat.image} 
                        alt={cat.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="text-xs md:text-sm font-medium text-gray-700">{cat.name}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Business Opportunity Section */}
          <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-4">
              {/* Header */}
              <div className="mb-10">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                  Discover your next business opportunity
                </h2>
                <p className="text-gray-600">Wholesale products at the best prices from verified suppliers</p>
              </div>
              
              {/* Tabs */}
              <div className="flex space-x-1 mb-8 overflow-x-auto pb-2 scrollbar-hide border-b-2 border-gray-100">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        flex items-center gap-2 pb-4 px-6 whitespace-nowrap font-semibold transition-all
                        border-b-2 -mb-0.5
                        ${activeTab === tab.id 
                          ? 'border-orange-500 text-orange-600' 
                          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                        }
                      `}
                    >
                      <Icon size={18} />
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Loading State for Regular Products */}
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                  {[...Array(10)].map((_, index) => (
                    <div key={index} className="bg-white rounded-xl overflow-hidden border border-gray-200 animate-pulse">
                      <div className="aspect-square bg-gray-200"></div>
                      <div className="p-3 space-y-2">
                        <div className="h-4 bg-gray-200 rounded"></div>
                        <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                        <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Product Grid - Using ProductsPage card design */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
                    {filteredProducts.slice(0, 10).map((product) => (
                      <div
                        key={product.id}
                        className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300 group cursor-pointer"
                      >
                        {/* Product Image */}
                        <div className="relative h-40 sm:h-48 overflow-hidden bg-gray-100">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.src = `https://via.placeholder.com/300x200?text=${encodeURIComponent(product.title.substring(0, 20))}`;
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-200">
                              <Package className="h-12 w-12 text-gray-400" />
                            </div>
                          )}
                          
                          {/* Badge */}
                          <div className="absolute top-2 left-2">
                            <span className={`text-[10px] font-semibold px-2 py-1 rounded border ${getBadgeColor(product.badge)}`}>
                              {product.badge}
                            </span>
                          </div>

                          {/* Favorite Button */}
                          <button 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(product.id);
                            }}
                            className="absolute top-2 right-2 p-1.5 bg-white rounded-full shadow hover:bg-gray-100"
                          >
                            <Heart className="w-4 h-4 text-gray-600" />
                          </button>

                          {/* Verified Badge */}
                          {product.verified && (
                            <div className="absolute bottom-2 left-2 bg-green-500 text-white px-2 py-0.5 rounded-full flex items-center gap-1 text-[10px] font-medium">
                              <Shield size={10} />
                              Verified
                            </div>
                          )}
                        </div>

                        {/* Product Info - Updated to match ProductsPage */}
                        <div className="p-3 sm:p-4">
                          {/* Price and Rating */}
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <div className="text-lg font-bold text-orange-600">
                                {formatINR(product.price)}
                              </div>
                              <div className="text-xs text-gray-500 mt-0.5">
                                FOB Price / Piece
                              </div>
                            </div>
                            <div className="flex items-center bg-orange-50 px-2 py-0.5 rounded">
                              <Star className="w-3 h-3 text-orange-400 fill-current" />
                              <span className="text-xs font-semibold text-orange-700 ml-1">
                                {product.rating}
                              </span>
                            </div>
                          </div>

                          {/* Product Name */}
                          <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 h-10">
                            {product.title}
                          </h3>

                          {/* Description Preview */}
                          {product.description && (
                            <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                              {product.description.substring(0, 60)}...
                            </p>
                          )}

                          {/* MOQ and Stock */}
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-3">
                            <span>
                              <span className="font-medium">MOQ:</span> {product.moq} {product.unit}
                            </span>
                            {product.stock > 0 ? (
                              <span className="text-green-600 font-medium">In Stock</span>
                            ) : (
                              <span className="text-red-600 font-medium">Out of Stock</span>
                            )}
                          </div>

                          {/* Seller Info */}
                          <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                            <div className="flex items-center">
                              <Building className="w-3 h-3 text-gray-400 mr-1.5" />
                              <span className="text-xs text-gray-600 truncate max-w-[100px]">
                                {product.sellerName}
                              </span>
                            </div>
                            {product.verified && (
                              <div className="flex items-center text-green-600 text-xs">
                                <Check className="w-3 h-3 mr-1" />
                                Verified
                              </div>
                            )}
                          </div>

                          {/* Action Buttons */}
                          <div className="mt-4 space-y-2">
                            {/* <button
                              onClick={() => handleViewDetails(product)}
                              className="w-full py-2 text-sm font-medium text-orange-600 border border-orange-300 rounded hover:bg-orange-50 transition-colors flex items-center justify-center"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View Details
                            </button> */}

                            <button
                              onClick={() => handleContactSupplier(product)}
                              className="w-full py-2.5 text-sm font-medium bg-orange-600 text-white rounded hover:bg-orange-700 transition-colors flex items-center justify-center"
                            >
                              <MessageCircle className="w-4 h-4 mr-2" />
                              Contact Supplier
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* View More Button */}
                  <div className="text-center mt-10">
                    <button className="inline-flex items-center gap-2 px-8 py-3 bg-white border-2 border-orange-500 text-orange-600 font-semibold rounded-lg hover:bg-orange-500 hover:text-white transition-all group">
                      View more products
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default LandingPage;